import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiResult } from '../auth/api-client';
import type { ReviewChecklistProduct } from '../shared/types';

interface SearchPage {
  items: ReviewChecklistProduct[];
  nextToken?: string;
  truncated?: boolean;
}
type FetchPage = (options: {
  query?: string;
  limit?: number;
  nextToken?: string;
}) => Promise<ApiResult<SearchPage>>;
interface SearchRun {
  active: boolean;
  busy: boolean;
  started: boolean;
  nextToken?: string;
  seen: Set<string>;
}

/** Bounded progressive search; cancellation stops follow-up reads and stale publication. */
export function useChecklistSearch(
  fetchPage: FetchPage,
  query: string,
  { enabled = true, retainItems = true, limit = 50 } = {},
) {
  const [items, setItems] = useState<ReviewChecklistProduct[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const runRef = useRef<SearchRun>();
  const [revision, setRevision] = useState(0);

  const load = useCallback(
    async (run: SearchRun) => {
      if (!run.active || run.busy || (run.started && run.nextToken === undefined)) return;
      run.busy = true;
      setLoading(true);
      setError(null);
      const budget = query.trim() === '' ? 1 : 4;
      try {
        for (let page = 0; page < budget && run.active; page += 1) {
          const requestedToken = run.nextToken;
          const result = await fetchPage({
            query,
            limit,
            ...(requestedToken === undefined ? {} : { nextToken: requestedToken }),
          });
          if (!run.active) return;
          if (!result.ok) throw new Error(result.message ?? result.error);
          setItems((current) => [
            ...new Map(
              [...current, ...result.data.items].map((item) => [item.checklistId, item]),
            ).values(),
          ]);
          if (requestedToken !== undefined) run.seen.add(requestedToken);
          const nextToken = result.data.nextToken;
          if (nextToken !== undefined && (nextToken === '' || run.seen.has(nextToken))) {
            // Do not keep following a broken cursor or imply that partial results are complete.
            run.started = false;
            run.nextToken = undefined;
            run.seen.clear();
            setHasMore(false);
            throw new Error('Search could not advance. Retry or narrow your search.');
          }
          run.started = true;
          run.nextToken = nextToken;
          setHasMore(nextToken !== undefined);
          if (nextToken === undefined) break;
        }
      } catch (failure) {
        if (run.active) {
          setError(
            failure instanceof Error ? failure.message : 'Could not search checklists. Try again.',
          );
        }
      } finally {
        run.busy = false;
        if (run.active) setLoading(false);
      }
    },
    [fetchPage, limit, query],
  );

  useEffect(() => {
    const run: SearchRun = { active: enabled, busy: false, started: false, seen: new Set() };
    runRef.current = run;
    setLoading(enabled);
    setError(null);
    setHasMore(false);
    if (!retainItems) setItems([]);
    const timer = window.setTimeout(
      () => {
        if (enabled) void load(run);
      },
      query.trim() === '' ? 0 : 200,
    );
    return () => {
      run.active = false;
      window.clearTimeout(timer);
    };
  }, [enabled, load, query, retainItems, revision]);

  const loadMore = useCallback(() => {
    if (runRef.current !== undefined) void load(runRef.current);
  }, [load]);
  const refresh = useCallback(() => setRevision((current) => current + 1), []);
  return { items, setItems, loading, error, hasMore, loadMore, retry: loadMore, refresh };
}
